import type { DsTone } from '@/ds/tone'
import { leaseStrings as s } from '@/features/lease/strings'
import type { Rent } from '@/types/models'
import { appLocale } from '@/i18n/locale.store'

export type RentDisplayStatus = 'paid' | 'partiallyPaid' | 'cancelled' | 'pending' | 'overdue' | 'due' | 'upcoming'

const dayKey = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/**
 * The one status of a rent, for the ledger, the month strip, the rent sheet and the pay sheet. A scheduled rent is
 * day-based, as the tenant dashboard is: overdue before today, due today, upcoming after (LP-647). A held claim is
 * `pending` (LP-696).
 */
export function rentDisplayStatus(r: Rent, now = new Date()): RentDisplayStatus {
  switch (r.status) {
    case 'paid':
      return 'paid'
    case 'paid-partially':
      return 'partiallyPaid'
    case 'canceled':
      return 'cancelled'
    case 'pending':
      return 'pending'
    default: {
      const diff = dayKey(new Date(r.rentDate)) - dayKey(now)
      return diff < 0 ? 'overdue' : diff === 0 ? 'due' : 'upcoming'
    }
  }
}

export const rentStatusTone: Record<RentDisplayStatus, DsTone> = { paid: 'success', partiallyPaid: 'warning', cancelled: 'neutral', pending: 'warning', overdue: 'danger', due: 'warning', upcoming: 'info' }

export const rentStatusLabel = (st: RentDisplayStatus) => s.rentStatus[st]

/** A tenant-logged offline payment the landlord has not confirmed. */
export const needsAcceptance = (r: Rent) => !!r.transactionId && r.transactionStatus === 'processing' && r.transactionMode === 'offline'

export const isSettled = (r: Rent) => r.status === 'paid' || r.status === 'paid-partially'

export const rentBalance = (r: Rent) => Math.max(r.expectedAmount - r.paidAmount, 0)

/** "{paid} of {total} months paid": a part payment counts 0.5; whole numbers drop the decimal. */
export function monthsPaidLabel(rents: Rent[], totalRents: number): string {
  const paid = rents.reduce((a, r) => a + (r.status === 'paid' ? 1 : r.status === 'paid-partially' ? 0.5 : 0), 0)
  const text = Number.isInteger(paid) ? String(paid) : paid.toFixed(1)
  return s.monthsPaid(text, totalRents)
}

/**
 * What a rent has really received: a held claim (`pending`) is not collected until the landlord accepts it, so only
 * the part paid before the claim counts (`paidAmount − heldAmount`; nothing when the backend does not send it).
 */
export function rentCollected(r: Rent): number {
  const paid = r.status === 'pending' ? (r.heldAmount === undefined ? 0 : r.paidAmount - r.heldAmount) : r.paidAmount
  return Math.max(Math.min(paid, r.expectedAmount), 0)
}

/** Collected vs still due over the loaded rents (cancelled rents excluded; a held claim is not collected, LP-697). */
export function ledgerTotals(rents: Rent[]): { collected: number; due: number } {
  const live = rents.filter((r) => r.status !== 'canceled')
  const collected = live.reduce((a, r) => a + rentCollected(r), 0)
  const expected = live.reduce((a, r) => a + r.expectedAmount, 0)
  return { collected, due: Math.max(expected - collected, 0) }
}

const monthYear = new Intl.DateTimeFormat(appLocale(), { month: 'short', year: 'numeric' })
const monthYearLong = new Intl.DateTimeFormat(appLocale(), { month: 'long', year: 'numeric' })
const dayLong = new Intl.DateTimeFormat(appLocale(), { day: 'numeric', month: 'long', year: 'numeric' })
const dateTime = new Intl.DateTimeFormat(appLocale(), { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
export const rentMonth = (r: Rent) => monthYear.format(new Date(r.rentDate))
export const rentMonthLong = (r: Rent) => monthYearLong.format(new Date(r.rentDate))
export const rentDayLong = (r: Rent) => dayLong.format(new Date(r.rentDate))
export const rentPaidOn = (r: Rent) => (r.updatedAt ? dateTime.format(new Date(r.updatedAt)) : '')

const csvCell = (v: string | number) => {
  const t = String(v)
  return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t
}

/** Ledger as CSV (oldest first): month, rent date, expected, paid, balance, status, paid on, reference. */
export function ledgerCsv(rents: Rent[], now = new Date()): string {
  const header = ['Month', 'Rent date', 'Expected', 'Paid', 'Balance', 'Status', 'Paid on', 'Reference']
  const rows = [...rents]
    .sort((a, b) => Date.parse(a.rentDate) - Date.parse(b.rentDate))
    .map((r) => [rentMonth(r), r.rentDate.slice(0, 10), r.expectedAmount, r.paidAmount, rentBalance(r), rentStatusLabel(rentDisplayStatus(r, now)), r.updatedAt && isSettled(r) ? r.updatedAt.slice(0, 10) : '', r.transactionId ?? ''])
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\n')
}

export function downloadText(filename: string, text: string, type = 'text/csv;charset=utf-8') {
  const blob = new Blob([text], { type })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** The earliest rent with a balance still to pay (not cancelled, not a payment in flight): the "Next due" fact. */
export function nextOpenRent(rents: Rent[]): Rent | undefined {
  return [...rents]
    .filter((r) => r.status !== 'canceled' && r.status !== 'pending' && rentBalance(r) > 0)
    .sort((a, b) => Date.parse(a.rentDate) - Date.parse(b.rentDate))[0]
}
